import {User} from './user.interface';
import {EdgeArrowType, NodePropertiesDto, OtherLinksMode, OtherNodesMode, SmapletComments} from '@maporium-workspace/shared';
import {NodeStateData} from '../../../../../maporium/src/app/entities/types/node-state-data';
import {LinkStateData} from '../../../../../maporium/src/app/entities/types/link-state.data';
import {MetaData} from './metadata';
import {LinkViewMode} from '../services/setting.service';

export interface SerializedMap {
  map: GraphMap,
  nodes: GraphNode[],
  links: GraphNodeLink[],
  overlayElements?: any[]
}

export interface GraphMap {
  id: string;
  name: string;
  description?: string;
  isTemplate: boolean;
  allowViewersToCreateFromTemplate?: boolean;
  isStarter?: boolean;
  availableToAll?: boolean;
  starterActivatedAt?: Date | null;
  owner: User;
  nodes?: GraphNode[];
  aiGeneratedDescription?: string;
  createdAt?: Date;
  updatedAt?: Date;
  sharedWith?: User[] | undefined | null;
  metadata?: MetaData[];
  isReadonly?: boolean | undefined | null;
  isUnseen?: boolean;
  hasGuestComments?: boolean;
  smapletComments?: SmapletComments;
  isPublic: boolean | undefined;
  allowWebsiteEmbedding?: boolean;
  smapletLinkViewMode?: LinkViewMode;
  smapletOtherLinksMode?: OtherLinksMode;
  smapletOtherNodesMode?: OtherNodesMode;
  smapletOpeningStoryId?: string | null;
  smapletOpeningSceneId?: string | null;
  smapletOpeningSilent?: boolean;
  smapletView?: '2d' | '3d' | null;
  smapletAutoOrbit?: boolean | null;
  includeViewsAndTours?: boolean;
  userRoles: SmapUserRoles[];
  automaticLayout?: string | null;
  thumbnailUrl?: string;
  publisherNameOption?: 'default' | 'owner' | 'organization';
  publisherName?: string;
  aiUsageBySkill?: Record<string, { credits: number; count: number; durationMs: number }>;
  aiCreditsUsed?: number;
  styles: {
    appearance?: string;
    color: string;
    colorIntensity: number;
    backgroundImageUrl: string;
    opacity: number; // 0-1
    backgroundSoundUrl?: string;
    backgroundSoundVolume: number; // 0-1, default: 0.5
    backgroundImageSize?: number; // slider value V in [-1, +1], default: 0
    backgroundImageCenter?: { x: number; y: number }; // model-space center, set once at creation
  };
}

export interface GroupedTemplates {
  owned: GraphMap[];
  shared: GraphMap[];
  store: GraphMap[];
}

export interface SmapUserRoles {
  id: string;
  role: string;
  user: User;
}
export interface GraphNode {
  id?: string;
  name?: string;
  description?: string;
  imageUrl?: string;
  color?: string;
  // @deprecated will be removed in future versions
  hasBorder?: boolean;
  properties?: NodePropertiesDto;
  type?: string;
  excludedFromState?: boolean;
  children: GraphNode[];
  parent?: GraphNode;
  weight?: number;
  map?: GraphMap;
  showBorder?: boolean;
  collapsed?: boolean;
  metadata?: MetaData[];
  x?: number;
  y?: number;
  links?: GraphNodeLink[] | null;
  sharedWith?: GraphNode[];
  reset?: boolean;
  states?: NodeStateData[];
  hasComments?: boolean;
  aiGeneratedDescription?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface GraphNodeLink {
  id?: string;
  from: GraphNode;
  to: GraphNode;

  name?: string;
  description?: string;
  color?: string;
  label?: string;
  weight?: number | string;
  metadata?: MetaData[];
  type?: LinkType | string;
  shape?: LinkShape | string;
  direction?: LinkDirection | string;
  sourceEndpoint?: 'top' | 'right' | 'bottom' | 'left' | null;
  targetEndpoint?: 'top' | 'right' | 'bottom' | 'left' | null;
  excludedFromState?: boolean;
  cyedgebendeditingWeights?: number[];
  cyedgebendeditingDistances?: number[];
  cyedgecontroleditingWeights?: number[];
  cyedgecontroleditingDistances?: number[];
  bendPointPositions?: { x: number, y: number }[];
  controlPointPositions?: { x: number, y: number }[];
  states?: LinkStateData[];
  textFontSize?: number | string;
  edgeArrowType?: EdgeArrowType;
  origin?: GraphNodeLink;
  reset?: boolean;
  hasComments?: boolean;
  soundUrl?: string | null;
  labelSlope?: 'straight' | 'sloped' | 'curved';
  aiGeneratedDescription?: string;
  linkLabelPosition?: number; // -1 to 1, default 0
  linkLabelOffset?: number; // -20 to 20, default -5
  createdAt?: string | null;
  updatedAt?: string | null;
}

export type LinkType = 'BEZIER' | 'UNBUNDLED_BEZIER' | 'HAYSTACK' | 'SEGMENTS' | 'TAXI' | 'STRAIGHT_TRIANGLE';
export type LinkShape = 'solid' | 'dashed' | 'dotted';
export type LinkDirection = 'directed' | 'undirected';
export const LinkDirectionMap = [
  {key: 'directed', value: 'directed', icon: 'trending_flat'},
  {key: 'bidirected', value: 'bidirected', icon: 'width'},
  {key: 'undirected', value: 'undirected', icon: 'horizontal_rule'},
]


export const EdgeTypeMapCy = {
  BEZIER: 'bezier',
  UNBUNDLED_BEZIER: 'unbundled-bezier',
  HAYSTACK: 'haystack',
  SEGMENTS: 'segments',
  STRAIGHT: 'straight',
  TAXI: 'taxi',
  STRAIGHT_TRIANGLE: 'straight-triangle',
}
export const EdgeTypeMapVisual = [
  {key: 'BEZIER', value: 'straight', icon: 'pen_size_2'},
  { key: 'STRAIGHT', value: 'segmented', icon: 'show_chart' },
  {key: 'UNBUNDLED_BEZIER', value: 'curved', icon: 'line_curve'},
]
export const EdgeShapeMap = {
  solid: 'solid',
  dashed: 'dashed',
  dotted: 'dotted',
}

export const EdgeShapeMapVisual = [
  {key: 'solid', value: 'solid', icon: 'horizontal_rule' },
  {key: 'dashed', value: 'dashed', icon: 'unknown_med' },
  {key: 'dotted', value: 'dotted', icon: 'more_horiz'},
]

export const LinkLabelSlopeMap = [
  {key: 'straight', value: 'straight', icon: 'text_rotate_vertical'},
  {key: 'sloped', value: 'sloped', icon: 'text_rotation_down'},
  // TODO: Implement curved label functionality - requires rendering labels along link shape
  // {key: 'curved', value: 'curved', icon: 'text_rotation_angleup'}
];

export const EdgeArrowTypeMapVisual = [
  {key: 'triangle', value: 'arrow', icon: 'line_end_arrow'},
  {key: 'triangle-backcurve', value: 'arrow, curved notch', icon: 'line_end_arrow_notch'},
  {key: 'vee', value: 'arrow, straight notch', icon: 'line_end_arrow_notch'},
  {key: 'chevron', value: 'chevron', icon: 'east'},
  {key: 'circle', value: 'circle', icon: 'line_end_circle'},
  {key: 'diamond', value: 'diamond', icon: 'line_end_diamond'},
  {key: 'square', value: 'square', icon: 'line_end_square'}
]


export const NodeShapeMapVisual = [
  { key: 'solid', value: 'solid', icon: 'horizontal_rule' },
  { key: 'dashed', value: 'dashed', icon: 'unknown_med' },
  { key: 'dotted', value: 'dotted', icon: 'more_horiz' }
];

export const NodeTypesCyto = [
  'ELLIPSE',
  'RECTANGLE',
  'TRIANGLE',
  'DIAMOND',
  'ROUND-DIAMOND',
  'ROUND-TRIANGLE',
  'ROUND-RECTANGLE',
  'BOTTOM-ROUND-RECTANGLE',
  'CUT-RECTANGLE',
  'BARREL',
  'RHOMBOID',
  'HEXAGON',
  'ROUND-HEXAGON',
  'HEPTAGON',
  'ROUND-HEPTAGON',
  'OCTAGON',
  'ROUND-OCTAGON',
  'STAR',
  'PENTAGON',
  'ROUND-PENTAGON',
  'TAG',
  'ROUND-TAG',
  'VEE',
];

export const NodeTypesVisual = [
  {key: 'ELLIPSE', value: 'circle', icon: 'circle'},
  {key: 'TRIANGLE', value: 'triangle', icon: 'change_history'},
  {key: 'RECTANGLE', value: 'square', icon: 'square'},
  {key: 'PENTAGON', value: 'pentagon', icon: 'pentagon'},
  {key: 'HEXAGON', value: 'hexagon', icon: 'hexagon'},
  // {key: 'HEPTAGON', value: 'heptagon'},
  // {key: 'OCTAGON', value: 'octagon'},
]

export const GraphNodeMapKey = 'graph-node-map';
export const GraphNodeMapListKey = 'graph-node-map-list';

export function readSelectedMap(): string | null {
  return sessionStorage.getItem(GraphNodeMapKey) ?? localStorage.getItem(GraphNodeMapKey);
}

export function writeSelectedMap(value: string): void {
  sessionStorage.setItem(GraphNodeMapKey, value);
  localStorage.setItem(GraphNodeMapKey, value);
}

export function clearSelectedMap(): void {
  sessionStorage.removeItem(GraphNodeMapKey);
  localStorage.removeItem(GraphNodeMapKey);
}

export interface MultiUpdatePayload {
  nodeIds: string[],
  linkIds: string[],
  stateId: string,
  diff: {
    node?: Partial<GraphNode>,
    link: Partial<GraphNodeLink>
  }
}


export const GraphLayouts: { key: LayoutType, value: string, icon?: string, isSeparatorAfter?: boolean }[] = [
  // Standard (core) layouts
  { key: 'preset', value: 'manual', icon: 'left_click', isSeparatorAfter: true },
  { key: 'cose', value: 'force-directed', icon: 'close_fullscreen' },
  { key: 'concentric', value: 'concentric', icon: 'target' },
  { key: 'breadthfirst', value: 'hierarchical', icon: 'tenancy' },
  { key: 'circle', value: 'circular', icon: 'panorama_fish_eye' },
  { key: 'random', value: 'random', icon: 'shuffle' },
  { key: 'grid', value: 'latticed', icon: 'grid_on', isSeparatorAfter: true },

  // Plugin-based (non-standard) layouts
  { key: 'cose-bilkent', value: 'cose-bilkent', icon: 'hub' },
  { key: 'fcose', value: 'fcose', icon: 'space_dashboard' },
  { key: 'cola', value: 'cola', icon: 'polyline' },
  { key: 'dagre', value: 'dagre', icon: 'account_tree' },
  { key: 'euler', value: 'euler', icon: 'device_hub' },
  { key: 'klay', value: 'klay', icon: 'layers_clear' },
  // { key: 'cise', value: 'cise', icon: 'scatter_plot' }
];


export function GetGraphLayoutByType(type: LayoutType) {
  return GraphLayouts.find(layout => layout.key === type);
}

export type LayoutType =
  | 'preset'
  | 'cose'
  | 'concentric'
  | 'breadthfirst'
  | 'circle'
  | 'random'
  | 'grid'
  | 'cose-bilkent'
  | 'fcose'
  | 'cola'
  | 'dagre'
  | 'euler'
  | 'klay'
  | 'cise';

